import type { paths as campaignPaths } from "../types/campaign-api.generated";
import * as api from "./client";
import { researchCms, type RankingEntity } from "./researchCms.service";
import type { TaxonomyMode } from "./submissions.service";

// ============================================================================
// Type Definitions
// ============================================================================
//
// NOTE: The staff-bff proxy for `/api/v1/mailing-groups` and
// `/api/v1/editorial-categories/countries-details` is being added in parallel
// (see services/staff-bff/src/services/discoveryRoutes.ts). Once those land in
// the generated OpenAPI spec, regenerate `types/api.generated.ts` and replace
// the interim types below with the `paths[...]` aliases (as
// editorialCategories.service.ts does). They are defined manually here so the
// feature can be built/typed ahead of the generated types.

/** Bar groups are split into two categories; standard groups have no category. */
export type MailingGroupCategory = "instructing_solicitor" | "other" | null;

// Effective status of a group's most-recent campaign (highest sequence); `null`
// when the group has no campaigns. Sourced from the regenerated OpenAPI type so
// the enum can't drift from the backend.
export type MailingGroupCampaignStatus =
  campaignPaths["/api/v1/mailing-groups"]["get"]["responses"]["200"]["content"]["application/json"]["groups"][number]["campaign_status"];

export type MailingGroup = {
  id: number;
  publication_id: number | null;
  edition_id: string | null;
  publication_name: string | null;
  edition: string;
  name: string;
  language_code: string | null;
  category: MailingGroupCategory;
  region_count: number;
  campaign_status: MailingGroupCampaignStatus;
  // Business Rule 5: true if ANY of the group's campaigns is scheduled/sending/
  // sent — locks Edit/Delete. Distinct from campaign_status (latest campaign,
  // for display): a sent invitation locks the group even if a later reminder is
  // still draft.
  campaign_started: boolean;
  // Survey-response coverage for the group's active referees (deduped by email):
  // how many have responded out of the total. Rendered as an "M/N" fraction.
  responded: number;
  total_referees: number;
  // When the group's campaign was first started (invitation sent), ISO 8601, or
  // null when no campaign has started yet.
  campaign_started_at: string | null;
  last_synced_at: string | null;
  created_at: string;
};

// ----------------------------------------------------------------------------
// Detail (getById): a single mailing group with its assigned regions.
// ----------------------------------------------------------------------------

export type MailingGroupDetailRegion = {
  region_id: number | null;
  ranking_entity_id: string | null;
  region_name: string | null;
};

export type MailingGroupDetailDto = {
  id: string;
  name: string;
  category: MailingGroupCategory;
  publication_id: number | null;
  edition_id: string | null;
  regions: MailingGroupDetailRegion[];
};

export type MailingGroupsPagination = {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

export type ListMailingGroupsResponse = {
  groups: MailingGroup[];
  pagination: MailingGroupsPagination;
  requestId: string | null;
};

export type ListMailingGroupsParams = {
  edition?: string;
  publicationId?: number;
  editionId?: string;
  taxonomy?: TaxonomyMode;
  limit?: number;
  offset?: number;
};

// ----------------------------------------------------------------------------
// Summary: one entry per (publication, edition) that has at least one mailing
// group, with the number of groups. Powers the aggregated Level-1 view.
// ----------------------------------------------------------------------------

export type MailingGroupsSummaryItem = {
  /**
   * Synthetic stable row key, `${publicationId}-${edition}`. Computed client-side
   * (the backend response has no `id`); a publication can appear in multiple
   * editions under "All editions", so publicationId alone is not unique.
   */
  id: string;
  publicationId: number | null;
  editionId: string | null;
  publicationName: string | null;
  /** Publication's own edition text; null on groups created before it was captured. */
  publicationEdition: string | null;
  edition: string;
  groupCount: number;
  /** Number of campaigns across the publication/edition's mailing groups. */
  campaignCount: number;
};

/** Wire shape returned by the backend (no synthetic `id`). */
type RawMailingGroupsSummaryItem = Omit<MailingGroupsSummaryItem, "id">;

type RawMailingGroupsSummaryResponse = {
  items: RawMailingGroupsSummaryItem[];
  requestId: string | null;
};

export type MailingGroupsSummaryResponse = {
  items: MailingGroupsSummaryItem[];
  requestId: string | null;
};

export type MailingGroupsSummaryParams = {
  edition?: string;
  taxonomy?: TaxonomyMode;
};

export type CreateMailingGroupRegion = {
  regionId: number;
  regionName?: string;
  languageCode?: string;
};

export type CreateNewAxisMailingGroupRegion = {
  rankingEntityId: string;
  regionName?: string;
  languageCode?: string;
};

type CreateMailingGroupFields = {
  publicationName?: string;
  /**
   * The publication's own edition text from taxonomy, e.g.
   * "2027 Edition (Asia Pacific)". Several publications share a name and year
   * and differ only here, so this is what keeps them apart on screen.
   */
  publicationEdition?: string;
  edition: string;
  name?: string;
  languageCode?: string;
};

export type CreateMailingGroupBody =
  | (CreateMailingGroupFields & {
      publicationId: number;
      regions: CreateMailingGroupRegion[];
    })
  | (CreateMailingGroupFields & {
      editionId: string;
      regions: CreateNewAxisMailingGroupRegion[];
    });

/**
 * Polymorphic create response: a standard selection produces a single group,
 * while an all-bar selection produces two categorised groups (instructing /
 * other). Callers only `await` the call; they don't read the result shape.
 */
export type CreateMailingGroupResponse =
  | { group: MailingGroup; requestId: string | null }
  | { groups: MailingGroup[]; requestId: string | null };

export type UpdateMailingGroupBody = {
  name?: string;
  languageCode?: string;
  regions?: CreateMailingGroupRegion[] | CreateNewAxisMailingGroupRegion[];
};

// ----------------------------------------------------------------------------
// Region picker source: countries-details for a publication.
// A region is a BAR region when `type === "Bar"`.
// ----------------------------------------------------------------------------

export const BAR_REGION_TYPE = "Bar";

export type CountryDetail = {
  id: number | null;
  name: string | null;
  type: string | null;
};

export type CountriesDetailsResponse = {
  countries: CountryDetail[];
  requestId: string | null;
};

export function isBarRegion(country: Pick<CountryDetail, "type">): boolean {
  return country.type === BAR_REGION_TYPE;
}

export const BAR_RANKING_ENTITY_TYPE = "bar";

export function isBarRankingEntity(entity: Pick<PickerRegion, "type">): boolean {
  return entity.type === BAR_RANKING_ENTITY_TYPE;
}

export type PickerRegion = {
  id: number | string | null;
  name: string | null;
  type: string | null;
};

const RANKING_ENTITY_PAGE_SIZE = 2000;

// ----------------------------------------------------------------------------
// Used regions: regions already assigned to a mailing group within the same
// publication + edition. Surfacing these lets the UI disable them up-front and
// pre-empt the 409 REGION_ALREADY_USED error (business Rule 4).
// ----------------------------------------------------------------------------

export type UsedRegion = {
  regionId: number;
  regionName: string;
  mailingGroupId: string;
  mailingGroupName: string;
};

export type UsedRegionsResponse = {
  usedRegions: UsedRegion[];
  requestId: string | null;
};

export type UsedRankingEntity = {
  regionId: number | null;
  rankingEntityId: string | null;
  regionName: string | null;
  mailingGroupId: string;
  mailingGroupName: string | null;
};

export type UsedRankingEntitiesResponse = {
  usedRegions: UsedRankingEntity[];
  requestId: string | null;
};

// ----------------------------------------------------------------------------
// Sync run: the baseline copy of referees into a mailing group. The backend is
// one-shot-then-lock — a second POST while in-flight just returns the current
// run. GET returns the latest run, or 404 when the group was never synced.
// Fields are snake_case to mirror the campaign-api wire shape verbatim.
// ----------------------------------------------------------------------------

export type SyncRunStatus = "queued" | "running" | "completed" | "failed";

export type SyncRunResponse = {
  id: number;
  mailing_group_id: number;
  run_id: string;
  kind: string;
  status: SyncRunStatus;
  total_expected: number | null;
  processed: number;
  inserted: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
};

export type SyncRunEnvelope = {
  sync_run: SyncRunResponse;
  request_id: string | null;
};

// ----------------------------------------------------------------------------
// Response summary: reminder-audience context for a mailing group — how many
// active referees (deduped by email) have responded vs still to remind, plus
// whether the invitation was already sent.
// ----------------------------------------------------------------------------

type ResponseSummaryResponse =
  campaignPaths["/api/v1/mailing-groups/{id}/response-summary"]["get"]["responses"]["200"]["content"]["application/json"];

export type MailingGroupResponseSummary = {
  totalReferees: number;
  responded: number;
  nonResponders: number;
  invitationSent: boolean;
};

// ============================================================================
// Service
// ============================================================================

function buildListEndpoint(params: ListMailingGroupsParams): string {
  const searchParams = new URLSearchParams();
  if (params.edition) searchParams.append("edition", params.edition);
  if (params.publicationId !== undefined)
    searchParams.append("publicationId", String(params.publicationId));
  if (params.editionId !== undefined) searchParams.append("editionId", params.editionId);
  if (params.taxonomy !== undefined) searchParams.append("taxonomy", params.taxonomy);
  if (params.limit !== undefined) searchParams.append("limit", String(params.limit));
  if (params.offset !== undefined) searchParams.append("offset", String(params.offset));
  const query = searchParams.toString();
  return `/v1/mailing-groups${query ? `?${query}` : ""}`;
}

function buildSummaryEndpoint(params: MailingGroupsSummaryParams): string {
  const searchParams = new URLSearchParams();
  if (params.edition) searchParams.append("edition", params.edition);
  if (params.taxonomy !== undefined) searchParams.append("taxonomy", params.taxonomy);
  const query = searchParams.toString();
  return `/v1/mailing-groups/summary${query ? `?${query}` : ""}`;
}

export const mailingGroupsService = {
  list: (params: ListMailingGroupsParams = {}): Promise<ListMailingGroupsResponse> =>
    api.get<ListMailingGroupsResponse>(buildListEndpoint(params)),

  getSummary: async (
    params: MailingGroupsSummaryParams = {},
  ): Promise<MailingGroupsSummaryResponse> => {
    const response = await api.get<RawMailingGroupsSummaryResponse>(buildSummaryEndpoint(params));
    return {
      ...response,
      items: response.items.map(item => ({
        ...item,
        id: `${item.publicationId ?? item.editionId}-${item.edition}`,
      })),
    };
  },

  getById: (id: string | number): Promise<{ group: MailingGroupDetailDto }> =>
    api.get<{ group: MailingGroupDetailDto }>(`/v1/mailing-groups/${id}`),

  create: (body: CreateMailingGroupBody): Promise<CreateMailingGroupResponse> =>
    api.post<CreateMailingGroupResponse>("/v1/mailing-groups", body),

  update: (
    id: string | number,
    body: UpdateMailingGroupBody,
  ): Promise<{ group: MailingGroup; requestId: string | null }> =>
    api.patch<{ group: MailingGroup; requestId: string | null }>(`/v1/mailing-groups/${id}`, body),

  remove: (id: string | number): Promise<void> => api.del<void>(`/v1/mailing-groups/${id}`),

  getCountriesDetails: (publicationId: number): Promise<CountriesDetailsResponse> =>
    api.get<CountriesDetailsResponse>(
      `/v1/editorial-categories/countries-details?publicationId=${publicationId}`,
    ),

  getUsedRegions: (publicationId: number, edition: string): Promise<UsedRegionsResponse> =>
    api.get<UsedRegionsResponse>(
      `/v1/mailing-groups/used-regions?publicationId=${publicationId}&edition=${encodeURIComponent(
        edition,
      )}`,
    ),

  getEditionRankingEntities: async (editionId: string): Promise<RankingEntity[]> => {
    const response = await researchCms.getRankingEntities({
      editionId,
      limit: RANKING_ENTITY_PAGE_SIZE,
    });
    if (response.pagination.hasMore) {
      throw new Error(
        `Edition ${editionId} has more than ${RANKING_ENTITY_PAGE_SIZE} ranking entities`,
      );
    }
    return response.researchRankingEntities;
  },

  getUsedRankingEntities: (editionId: string): Promise<UsedRankingEntitiesResponse> =>
    api.get<UsedRankingEntitiesResponse>(
      `/v1/mailing-groups/used-regions?editionId=${encodeURIComponent(editionId)}`,
    ),

  startSync: (id: string | number): Promise<SyncRunEnvelope> =>
    api.post<SyncRunEnvelope>(`/v1/mailing-groups/${id}/sync`),

  getLatestSyncRun: (id: string | number): Promise<SyncRunEnvelope> =>
    api.get<SyncRunEnvelope>(`/v1/mailing-groups/${id}/sync`),

  getResponseSummary: (id: string | number): Promise<MailingGroupResponseSummary> =>
    api.get<ResponseSummaryResponse>(`/v1/mailing-groups/${id}/response-summary`).then(r => ({
      totalReferees: r.total_referees,
      responded: r.responded,
      nonResponders: r.non_responders,
      invitationSent: r.invitation_sent,
    })),
};
